import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Database, Globe } from 'lucide-react'
import './RawAccountingProofWidget.css'

/*
 * RawAccountingProofWidget
 * prefix: rap-
 *
 * Consumes Codex proof artifacts (read-only):
 * - /data/rawArchiveManifest.generated.json   (per-book raw/parsed file counts)
 * - /data/liveOverlapAudit.generated.json     (live receiver overlap by sport)
 * - /data/proofs/*.json                        (single-book proof results)
 *
 * Does NOT touch receiver, canonicalization, or normalization.
 */

const PROOF_FILES = [
  { book: 'Winamax', path: '/data/proofs/winamax_proof.json' },
  { book: 'Matchbook', path: '/data/proofs/matchbook_proof.json' },
  { book: 'BetOpenly', path: '/data/proofs/betopenly_proof.json' },
  { book: 'Betway', path: '/data/proofs/betway_proof.json' },
]

function StatusBadge({ status }) {
  if (status === 'proved') return <span className="rap-badge rap-proved"><CheckCircle2 size={11} /> Proved</span>
  if (status === 'partial') return <span className="rap-badge rap-partial"><AlertTriangle size={11} /> Partial</span>
  return <span className="rap-badge rap-unproved"><XCircle size={11} /> Unproved</span>
}

export default function RawAccountingProofWidget() {
  const [manifest, setManifest] = useState(null)
  const [overlap, setOverlap] = useState(null)
  const [proofs, setProofs] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [manifestRes, overlapRes] = await Promise.all([
        fetch('/data/rawArchiveManifest.generated.json').then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/data/liveOverlapAudit.generated.json').then(r => r.ok ? r.json() : null).catch(() => null),
      ])

      const proofResults = {}
      for (const pf of PROOF_FILES) {
        try {
          const res = await fetch(pf.path)
          if (res.ok) proofResults[pf.book] = await res.json()
        } catch { /* skip */ }
      }

      setManifest(manifestRes)
      setOverlap(overlapRes)
      setProofs(proofResults)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Build per-book rows from manifest + proofs
  const bookRows = []
  if (manifest?.books) {
    for (const b of manifest.books) {
      const name = b.bookmaker
      const nameLower = name.toLowerCase()
      const proof = Object.entries(proofs).find(([k]) => k.toLowerCase() === nameLower)?.[1]
      let status = 'unproved'
      let rawRows = 0, parsedRows = 0, matchedRows = 0, unmatchedRows = 0

      if (proof) {
        rawRows = proof.raw_buttons || 0
        parsedRows = proof.parsed_outcomes || 0
        matchedRows = proof.matched_rows || 0
        unmatchedRows = proof.unmatched_rows || 0
        if (proof.audit_status === 'summary_only_raw_artifact') {
          status = 'partial'
        } else {
          status = unmatchedRows === 0 && matchedRows > 0 ? 'proved' : 'partial'
        }
      }

      bookRows.push({
        name,
        rawFiles: b.raw_json_count || 0,
        outcomeFiles: b.parsed_outcomes_count || 0,
        sports: (b.sports || []).filter(s => s !== 'unknown'),
        latestModified: b.latest_modified_at,
        status,
        rawRows,
        parsedRows,
        matchedRows,
        unmatchedRows,
      })
    }
  }

  // Sort: proved first, then by raw files descending
  bookRows.sort((a, b) => {
    const order = { proved: 0, partial: 1, unproved: 2 }
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status]
    return b.rawFiles - a.rawFiles
  })

  const provedCount = bookRows.filter(b => b.status === 'proved').length
  const totalBooks = bookRows.length
  const booksWithRaw = bookRows.filter(b => b.rawFiles > 0).length

  // Live overlap summary
  const overlapLeagues = overlap?.leagues || []

  return (
    <div className="widget rap-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Database size={16} />
          Raw Accounting Proof Campaign
        </h3>
        <button className="widget-refresh-btn" onClick={fetchAll} title="Refresh">
          <RefreshCw size={14} className={loading ? 'rap-spinning' : ''} />
        </button>
      </div>

      {error && <div className="rap-error">{error}</div>}

      {/* Campaign summary */}
      <div className="rap-summary-grid">
        <div className="rap-summary-card">
          <div className="rap-summary-value">{provedCount}</div>
          <div className="rap-summary-label">Books Proved</div>
        </div>
        <div className="rap-summary-card">
          <div className="rap-summary-value">{booksWithRaw}</div>
          <div className="rap-summary-label">Books w/ Raw</div>
        </div>
        <div className="rap-summary-card">
          <div className="rap-summary-value">{totalBooks}</div>
          <div className="rap-summary-label">Total Tracked</div>
        </div>
        <div className="rap-summary-card">
          <div className="rap-summary-value">{manifest?.artifact_counts?.raw_json || 0}</div>
          <div className="rap-summary-label">Raw Files</div>
        </div>
        <div className="rap-summary-card">
          <div className="rap-summary-value">{manifest?.artifact_counts?.parsed_outcomes || 0}</div>
          <div className="rap-summary-label">Parsed Files</div>
        </div>
        <div className="rap-summary-card">
          <div className="rap-summary-value">{overlap?.total_games || 0}</div>
          <div className="rap-summary-label">Live Games</div>
        </div>
      </div>

      {/* Live overlap by sport */}
      {overlapLeagues.length > 0 && (
        <div className="rap-section">
          <div className="rap-section-title">
            <Globe size={14} />
            Live Overlap by Sport
          </div>
          <div className="rap-table-wrap">
            <table className="rap-table">
              <thead>
                <tr>
                  <th>Sport</th>
                  <th>Games</th>
                  <th>Books</th>
                  <th>Max Overlap</th>
                </tr>
              </thead>
              <tbody>
                {overlapLeagues.map(lg => (
                  <tr key={lg.league}>
                    <td className="rap-league">{lg.league}</td>
                    <td>{lg.game_count}</td>
                    <td>{lg.unique_book_count}</td>
                    <td>
                      <span className={`rap-overlap ${lg.max_books_count >= 10 ? 'rap-high' : lg.max_books_count >= 5 ? 'rap-mid' : 'rap-low'}`}>
                        {lg.max_books_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-book proof table */}
      {bookRows.length > 0 && (
        <div className="rap-section">
          <div className="rap-section-title">
            <Database size={14} />
            Per-Book Raw Accounting ({provedCount}/{totalBooks} proved)
          </div>
          <div className="rap-table-wrap">
            <table className="rap-table">
              <thead>
                <tr>
                  <th>Book</th>
                  <th>Status</th>
                  <th>Raw Files</th>
                  <th>Outcome Files</th>
                  <th>Raw Rows</th>
                  <th>Parsed</th>
                  <th>Matched</th>
                  <th>Unmatched</th>
                  <th>Sports</th>
                </tr>
              </thead>
              <tbody>
                {bookRows.map(b => (
                  <tr key={b.name} className={b.status === 'proved' ? 'rap-row-proved' : ''}>
                    <td className="rap-book-name">{b.name}</td>
                    <td><StatusBadge status={b.status} /></td>
                    <td>{b.rawFiles}</td>
                    <td>{b.outcomeFiles}</td>
                    <td>{b.rawRows || '—'}</td>
                    <td>{b.parsedRows || '—'}</td>
                    <td className={b.matchedRows > 0 ? 'rap-green' : ''}>{b.matchedRows || '—'}</td>
                    <td className={b.unmatchedRows > 0 ? 'rap-red' : ''}>{b.unmatchedRows || '—'}</td>
                    <td className="rap-sports">{b.sports.length > 0 ? b.sports.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && !manifest && <div className="rap-empty">Loading proof data...</div>}
      {!loading && !manifest && !error && <div className="rap-empty">No proof artifacts found. Waiting for Codex to produce raw accounting proofs.</div>}

      {manifest && (
        <div className="rap-footer">
          Archive generated: {new Date(manifest.generated_at).toLocaleString()}
          {overlap && <> | Overlap audit: {new Date(overlap.generated_at).toLocaleString()}</>}
        </div>
      )}
    </div>
  )
}
